import React, { useEffect, useState } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { FileCog, Loader2, Download, Search, ShieldCheck, Copy } from "lucide-react";

export default function QmsPanel() {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [company, setCompany] = useState("");
  const [family, setFamily] = useState("");
  const [notes, setNotes] = useState("");
  const [content, setContent] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");

  useEffect(() => {
    api.get("/qms/sops/catalog").then(({ data }) => setCatalog(data.catalog || [])).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const grouped = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? catalog.filter((s) =>
          [s.title, s.code, s.cfr, s.iso, s.category].some((x) =>
            (x || "").toLowerCase().includes(q)
          )
        )
      : catalog;
    return filtered.reduce((acc, s) => {
      (acc[s.category] = acc[s.category] || []).push(s);
      return acc;
    }, {});
  }, [catalog, query]);

  const generate = async () => {
    if (!selected) return;
    setStreaming(true);
    setContent("");
    let buf = "";
    try {
      await streamSSE(
        "/qms/sops/generate",
        {
          sop_code: selected.code,
          company_name: company || "[Company Name]",
          device_family: family,
          project_id: projectId || null,
          notes,
        },
        (frame) => {
          if (frame.delta) {
            buf += frame.delta;
            setContent(buf);
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setStreaming(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(content);
    toast.success("SOP copied to clipboard");
  };

  const download = () => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selected.code}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="qms-root" className="max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">QMS SOP LIBRARY · 21 CFR 820 + ISO 13485</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          One-tap SOP generator.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Pick a Quality System procedure. Qualmitta drafts an audit-ready SOP with a compliant header,
          numbered procedure, records, and CFR/ISO references — ready to be signed by a QA manager.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Catalog */}
        <div className="lg:col-span-4">
          <div className="mb-3 flex items-center border border-slate-200 bg-white px-3">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              data-testid="qms-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SOPs, CFR sections…"
              className="flex-1 border-none bg-transparent px-2 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat}>
                <div className="overline mb-1.5 text-slate-400">{cat}</div>
                <ul className="space-y-1">
                  {list.map((s) => {
                    const active = selected?.code === s.code;
                    return (
                      <li key={s.code}>
                        <button
                          data-testid={`qms-sop-${s.code}`}
                          onClick={() => setSelected(s)}
                          className={`block w-full border-l-2 px-3 py-2 text-left text-xs transition-all ${
                            active
                              ? "border-[#2563EB] bg-[#2563EB]/10 text-white"
                              : "border-transparent text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          }`}
                        >
                          <div className="font-medium">{s.title}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-400">
                            <span className="font-mono">{s.cfr}</span>
                            <span className="text-slate-300">·</span>
                            <span>{s.iso}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Right - generator */}
        <div className="lg:col-span-8">
          {!selected ? (
            <div className="flex h-[60vh] flex-col items-center justify-center border border-dashed border-slate-200 bg-white text-center text-slate-500">
              <FileCog className="mb-3 h-8 w-8 text-[#2563EB]" strokeWidth={1.5} />
              <div className="text-sm">Pick an SOP from the catalog on the left.</div>
              <div className="mt-1 max-w-sm text-xs text-slate-400">24 procedures pre-mapped to 21 CFR 820 subparts and ISO 13485:2016 clauses.</div>
            </div>
          ) : (
            <div className="border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="overline mb-1 text-[#2563EB]">{selected.category.toUpperCase()}</div>
                    <div className="font-serif text-xl font-semibold text-slate-900">{selected.title}</div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      <span className="font-mono">{selected.cfr}</span> · {selected.iso}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-200">
                    <ShieldCheck className="h-3 w-3" /> AUDIT-READY
                  </span>
                </div>
              </div>

              <div className="grid gap-3 border-b border-slate-200 bg-[#FAFAF7] p-4 md:grid-cols-3">
                <input
                  data-testid="qms-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company name"
                  className="border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
                />
                <input
                  data-testid="qms-device-family"
                  value={family}
                  onChange={(e) => setFamily(e.target.value)}
                  placeholder="Device family (optional)"
                  className="border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
                />
                {projects.length > 0 ? (
                  <select
                    data-testid="qms-project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none"
                  >
                    <option value="">Attach to project (optional)</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                ) : (
                  <input placeholder="No projects yet" disabled className="border border-slate-200 bg-white px-3 py-2 text-sm text-slate-300" />
                )}
              </div>

              <div className="flex items-center gap-3 p-4">
                <textarea
                  data-testid="qms-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Any specifics: high-risk device, sterile, class III implantable, etc."
                  className="flex-1 border border-slate-200 bg-[#FAFAF7] px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
                />
                <button
                  data-testid="qms-generate-btn"
                  onClick={generate}
                  disabled={streaming}
                  className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-40"
                >
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCog className="h-4 w-4" />}
                  {content ? "Regenerate" : "Generate SOP"}
                </button>
              </div>

              {content && (
                <div className="border-t border-slate-200">
                  <div className="flex items-center justify-between border-b border-slate-200 bg-[#FAFAF7] px-4 py-2">
                    <div className="overline text-slate-500">SOP DRAFT</div>
                    <div className="flex items-center gap-2">
                      <button data-testid="qms-copy" onClick={copy} className="inline-flex items-center gap-1.5 border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-slate-300 hover:text-slate-900">
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                      <button data-testid="qms-download" onClick={download} className="inline-flex items-center gap-1.5 border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-slate-300 hover:text-slate-900">
                        <Download className="h-3 w-3" /> Markdown
                      </button>
                    </div>
                  </div>
                  <div data-testid="qms-output" className="max-h-[60vh] overflow-y-auto p-6">
                    <Markdown>{content}</Markdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
